import { Injectable } from '@angular/core';
import { DateParser } from './date.parser';
import {
  AbstractDateService,
  addDays,
  addMonths,
  addWeeks,
  addYears,
  endOfMonth,
  endOfYear,
  getLastDayOfMonth,
  getMonth,
  getYear,
  isIsoDate,
  IsoDate,
  IsoDateTime,
  IsoDateTimeTz,
  startOfMonth,
  startOfWeek,
  startOfYear
} from '@softline/core';

@Injectable()
export class IsoDateParser<T extends IsoDate | IsoDateTime | IsoDateTimeTz> extends DateParser<T> {

  constructor(private dateService: AbstractDateService<T>) {
    super()
  }

  parse(value: string): T | null {
    try {
      const date =
        this.parseTodayInput(value) ||
        this.parseMonthStartInput(value) ||
        this.parseMonthEndInput(value) ||
        this.parseTodayAndMonthInput(value) ||
        this.parseTodayAndYearInput(value) ||
        this.parseWeekInput(value) ||
        this.parseYearStartInput(value) ||
        this.parseYearEndInput(value) ||
        this.parseDayInput(value) ||
        this.parseDayAndMonthInput(value) ||
        this.parseDayMonthAndYearInput(value) ||
        this.parseLocaleInput(value) ||
        value;
      if(!date || !this.dateService.isValid(date))
        return null;
      return date;
    } catch (message) {
      return null;
    }
  }

  private parseTodayInput(value: string): string | null {
    if (value === 'h') return this.dateService.now();

    const match = value.match('^h([-|+]\\d+)$');
    if (match !== null)
      return addDays(this.dateService.now(), +match[1]);

    return null;
  }

  private parseMonthStartInput(value: string): string | null {
    if (value === 'ma')
      return startOfMonth(this.dateService.now());

    const match1 = value.match('^ma(\\d+)$');
    if (match1 !== null)
      return addMonths(startOfYear(this.dateService.now()), +match1[1] - 1);

    const match2 = value.match('^ma([-|+]\\d+)$');
    if (match2 !== null)
      return addMonths(startOfMonth(this.dateService.now()), +match2[1]);

    return null;
  }

  private parseMonthEndInput(value: string): string | null {
    if (value === 'me')
      return endOfMonth(this.dateService.now());

    const match = value.match('^me([-|+]\\d+)$');
    if (match != null)
      return endOfMonth(addMonths(this.dateService.now(), +match[1]));

    return null;
  }

  private parseTodayAndMonthInput(value: string): string | null {
    const match = value.match('^h([-+]\\d+)[mM]$');
    if (match != null)
      return addMonths(this.dateService.now(),+match[1]);

    return null;
  }

  private parseTodayAndYearInput(value: string): string | null {
    const match = value.match('^h([-+]\\d+)[jJ]$');
    if (match != null)
      return addYears(this.dateService.now(),+match[1]);

    return null;
  }

  private parseWeekInput(value: string): string | null {
    if (value === 'wa')
      return startOfWeek(this.dateService.now());

    const weekdays = ['mo', 'di', 'mi', 'do', 'fr', 'sa', 'so'];

    if (weekdays.indexOf(value) > -1)
      return addDays(startOfWeek(this.dateService.now()), weekdays.indexOf(value));

    const weekdaysAggregate = weekdays.join('|');
    const match1 = value.match(`^(${weekdaysAggregate})(\\d+)$`);
    if (match1 != null)
      return addWeeks(addDays(startOfWeek(startOfYear(this.dateService.now())), weekdays.indexOf(match1[1])), +match1[2] - 1)

    const match2 = value.match(`^(${weekdaysAggregate})([-|+]\\d+)$`);
    if (match2 != null)
      return addWeeks(addDays(startOfWeek(this.dateService.now()), weekdays.indexOf(match2[1])), +match2[2]);

    return null;
  }

  private parseYearStartInput(value: string): string | null {
    if (value === 'ja')
      return startOfYear(this.dateService.now());

    const match1 = value.match('^ja(\\d+)$');
    if (match1 != null)
      return `${this.getYear(+match1[1])}-01-01`;

    const match2 = value.match('^ja([-|+]\\d+)$');
    if (match2 != null)
      return addYears(startOfYear(this.dateService.now()), +match2[1]);

    return null;
  }

  private parseYearEndInput(value: string): string | null {
    if (value === 'je')
      return endOfYear(this.dateService.now());

    const match1 = value.match('^je(\\d+)$');
    if (match1 != null)
      return `${this.getYear(+match1[1])}-12-31`;

    const match2 = value.match('^je([-|+]\\d+)$');
    if (match2 != null)
      return addYears(endOfYear(this.dateService.now()), +match2[1]);

    return null;
  }

  private parseDayInput(value: string): string | null {
    const match = /^\d{1,2}$/g.exec(value);
    if (match == null) return null;

    const day = +match[0];
    const date = startOfMonth(this.dateService.now())
    const lastDayOfMonth = getLastDayOfMonth(getYear(date), getMonth(date));
    if (day <= 0 || day > lastDayOfMonth)
      throw Error(
        `Invalid day "${day}". Day has to be between 1 and ${lastDayOfMonth}.`
      );

    return addDays(date, day - 1);
  }

  private parseDayAndMonthInput(value: string): string | null {
    const match = /^(\d{2})(\d{1,2})$/g.exec(value);
    if (match == null) return null;

    let date = startOfYear(this.dateService.now());

    const day = +match[1];
    const month = +match[2];

    if (month <= 0 || month > 12)
      throw Error(
        `Invalid month "${month}". Month has to be between 1 and 12.`
      );

    date = addMonths(date, month - 1);
    const lastDayOfMonth = getLastDayOfMonth(getYear(date), getMonth(date));

    if (day <= 0 || day > lastDayOfMonth)
      throw Error(
        `Invalid day "${day}". Day has to be between 1 and ${lastDayOfMonth}}.`
      );

    return addDays(date, day-1);
  }

  private parseDayMonthAndYearInput(value: string): string| null {
    const match = /^(\d{1,2})(\d{1,2})(\d{2,4})$/g.exec(value);
    if (match == null || match[3].length === 3) return null;

    const day = match[1].padStart(2, '0');
    const month = match[2].padStart(2, '0');
    const year = this.getYear(+match[3]);

    return `${year}-${month}-${day}`;
  }

  private parseLocaleInput(value: string): string | null {
    const match = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/g.exec(value);
    if( match == null) return null;

    const day = +match[1];
    const month = +match[2];
    const year = +match[3];

    if (month <= 0 || month > 12)
      throw Error(
        `Invalid month "${month}". Month has to be between 1 and 12.`
      );

    const lastDayOfMonth = getLastDayOfMonth(year, month);
    if (day <= 0 || day > lastDayOfMonth)
      throw Error(
        `Invalid day "${day}". Day has to be between 1 and ${lastDayOfMonth}.`
      );

    return `${year}-${match[2].padStart(2, '0')}-${match[1].padStart(2, '0')}`;
  }

  private getYear(year: number) {
    if (year % 100 === year && year - 20 <= getYear(this.dateService.now()) % 100)
      year = year + 2000;
    else if (year % 100 === year) year = year + 1900;
    return year;
  }
}
